import { useEffect, useRef } from 'react';

/**
 * Makes a full-screen mobile sheet/dialog close on the OS/browser back
 * gesture instead of it navigating the whole app away. Chrome on Android
 * treats an edge swipe as browser-back; with no history entry of our own to
 * consume, that back-navigates the page the sheet was opened from. Pushing
 * one while open gives the gesture something to pop first (a `popstate`)
 * so it closes the sheet instead. Closing any other way (the X button,
 * overlay tap, Escape) consumes that same entry via `history.back()` so it
 * doesn't linger as a dead step in back-history.
 *
 * Returns an onOpenChange to pass to the sheet/dialog in place of `onClose`.
 */
export const useBackGestureDismiss = (open, onClose) => {
  const pushedRef = useRef(false);

  useEffect(() => {
    if (!open) return undefined;
    window.history.pushState({ pdDismiss: true }, '');
    pushedRef.current = true;
    const onPopState = () => {
      pushedRef.current = false;
      onClose(false);
    };
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
      pushedRef.current = false;
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (nextOpen) => {
    if (nextOpen === false && pushedRef.current) {
      window.history.back();
    } else {
      onClose(nextOpen);
    }
  };
};
